import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { DeprecatedConfirmationDialogComponent } from '@app/components';
import { Row } from '@app/components/simple-table/simple-table.component';
import { AnalyticEvents, FeatureFlag } from '@app/enums';
import { TemplateType } from '@app/modules/performance/enums/template-type.enum';
import { ReviewService } from '@app/modules/performance/services';
import { AnalyticService } from '@app/services';
import { AuthService } from '@app/services/auth.service';
import { FeatureService } from '@app/services/feature.service';
import { NotifyService } from '@app/services/notify.service';
import { StoreView } from '@common/views';
import { Employee } from '@models/employee/employee.model';
import { Review } from '@models/performance/review.model';
import { Template } from '@models/performance/template.model';

interface ReportsMap {
    self: Map<number, Employee>;
    peers: Map<number, Employee>;
    manager: Map<number, Employee>;
    directReports: Map<number, Employee>;
    others: Map<number, Employee>;
}

interface Report {
    subjectId: number;
    participants: number[];
}

@Component({
    templateUrl: './bulk-review-store.template.html',
    styleUrls: ['./bulk-review-store.style.scss'],
})
export class BulkReviewStoreView extends StoreView implements OnInit {
    @ViewChild('revokeConfirmationDialog') revokeConfirmationDialog: DeprecatedConfirmationDialogComponent;
    isLoading: boolean = false;
    missingTemplates: boolean = false;
    review: Review;
    reviewees: Employee[] = [];
    participants: Map<number, boolean> = new Map<number, boolean>();
    participantsList: Row[] = [];
    revieweesParticipantsList: Map<number, ReportsMap> = new Map<number, ReportsMap>();
    hasPerformanceModuleSplit = false;
    templates: Template[] = [];

    constructor(
        private auth: AuthService,
        private analyticService: AnalyticService,
        private featureService: FeatureService,
        private notify: NotifyService,
        private reviewService: ReviewService,
        private router: Router
    ) {
        super();
        this.resetReview();
    }

    async ngOnInit(): Promise<void> {
        this.hasPerformanceModuleSplit = await this.featureService.has(FeatureFlag.performanceModuleSplit);
        await this.setTemplates();
    }

    onSave(): void {
        this.isLoading = true;

        this.reviewService
            .onSave(this.review, this.getParticipantList())
            .then(() => {
                this.resetReview();
                this.notify.success('performance.bulk-review.reviewCreated');
                this.analyticService.trackEvent(AnalyticEvents.PerformanceReviewCreateReviewClickCompleteToSave);
                this.router.navigate(['/performance', 'reviews']);
            })
            .catch((err: HttpErrorResponse) => {
                this.notify.error(err || 'performance.bulk-review.reviewNotCreatedError');
            })
            .finally(() => {
                this.isLoading = false;
            });
    }

    updateReviewees(reviewees: Employee[]): void {
        this.reviewees = reviewees;
    }

    updateParticipantsTableList(rows: Row[]): void {
        this.participantsList = rows;
    }

    updateRevieweesParticipantsList(list: Map<number, ReportsMap>): void {
        this.revieweesParticipantsList = list;
    }

    shouldMoveToNextStep = (step: any): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
            // Only move to the next step if there are any reviewees added
            if (step.stepKey === 'selectReviewees' && this.reviewees.length === 0) {
                this.notify.error('performance.bulk-review.oneRevieweeMustBeSelected');
                reject();
            }

            if (step.stepKey === 'selectParticipants') {
                const participantsList = this.getParticipantList();

                const revieweesWithParticipantsList = participantsList.filter(
                    (participant) => participant.participants.length > 0
                );

                if (participantsList.length !== revieweesWithParticipantsList.length) {
                    this.notify.error('performance.bulk-review.oneParticipantTypeMustBeSelected');
                    reject();
                }
            }

            resolve();
            return;
        });
    };

    private getParticipantList(): Report[] {
        const participants: Report[] = [];
        for (let [key, value] of this.revieweesParticipantsList.entries()) {
            const values = [
                ...value.manager.values(),
                ...value.self.values(),
                ...value.directReports.values(),
                ...value.peers.values(),
                ...value.others.values(),
            ];

            const participantsList = values.map((participant) => participant.id);

            participants.push({
                subjectId: key,
                participants: participantsList,
            });
        }

        return participants;
    }

    async setTemplates(): Promise<void> {
        this.isLoading = true;

        try {
            const [templates] = await Template.param('company', this.auth.company.id)
                .where('hasQuestions', true)
                .orderBy('name', 'ASC')
                .when(this.hasPerformanceModuleSplit, (query) => query.where('type', TemplateType.REVIEW))
                .all();

            this.templates = templates;
            this.missingTemplates = !templates?.length;
        } catch (err) {
            this.notify.error(err);
        }

        this.isLoading = false;
    }

    private resetReview(): void {
        this.review = new Review({ reoccuring: 'never', viewResultsBeforeCompletion: false });
    }
}
